/** API of the stores referential (ports of StoreService and WorkshopService). */
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '@/shared/api/client';
import type { WithId } from '@/shared/api/types';
import type { ExclusiveSymbolKey } from '@/shared/constants';

const flags = <K extends string>(keys: readonly K[]) =>
  z
    .object(
      Object.fromEntries(
        keys.map((key) => [key, z.boolean().optional()]),
      ) as Record<K, z.ZodOptional<z.ZodBoolean>>,
    )
    .partial();

export const KEYBOARD_KEYS = [
  'latin',
  'arabic',
  'chinese',
  'chineseTw',
  'chineseHk',
  'cyrillic',
] as const;

export const storeSchema = z.object({
  id: z.string(),
  storeCode: z.string().nullish(),
  name: z.string().nullish(),
  fullAddress: z.string().nullish(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  currency: z.string().nullish(),
  country: z.string().nullish(),
  region: z.string().nullish(),
  language: z.string().nullish(),
  stockControl: z.boolean().nullish(),
  bypassHeadOfficeApproval: z.boolean().nullish(),
  bypassBoutiqueManagerApproval: z.boolean().nullish(),
  permanentInStoreWorkshop: z.boolean().nullish(),
  disabled: z.boolean().nullish(),
  centralFlowForbidden: z.boolean().nullish(),
  frontOfficeAccess: z
    .object({ abcdior: z.boolean().optional(), dms: z.boolean().optional() })
    .partial()
    .nullish(),
  iosKeyboard: z
    .object({
      active: z.boolean().optional(),
      keyboard: flags(KEYBOARD_KEYS).nullish(),
    })
    .partial()
    .nullish(),
  symbolActive: z.boolean().nullish(),
  exclusiveSymbolActive: z
    .object({
      active: z.boolean().optional(),
      symbols: z.record(z.string(), z.boolean()).nullish(),
    })
    .partial()
    .nullish(),
  iconicThreadsActive: z.boolean().nullish(),
  exclusiveThreadsActive: z.boolean().nullish(),
  workshopSavId: z.string().nullish(),
  workshopEventId: z.string().nullish(),
  workshopEventStart: z.string().nullish(),
  workshopEventEnd: z.string().nullish(),
});

export type StoreRecord = z.infer<typeof storeSchema>;

export const storesQueryOptions = () =>
  queryOptions({
    queryKey: ['stores'] as const,
    queryFn: () =>
      api.get<StoreRecord[]>('store', { schema: z.array(storeSchema) }),
    retry: false,
  });

export const workshopSchema = z.object({
  id: z.string(),
  name: z.string().nullish(),
});
export type Workshop = z.infer<typeof workshopSchema>;

export const workshopsQueryOptions = () =>
  queryOptions({
    queryKey: ['workshops'] as const,
    queryFn: () =>
      api.get<Workshop[]>('workshop', { schema: z.array(workshopSchema) }),
    staleTime: 60_000,
    retry: false,
  });

export type StoreKeyboardKey = (typeof KEYBOARD_KEYS)[number];

/** Keyboards a store can switch on: all but the always-on latin one. */
export type OptionalStoreKeyboardKey = Exclude<StoreKeyboardKey, 'latin'>;

/** Payload of the create / update calls. */
export interface StoreInput
  extends
    Record<`${OptionalStoreKeyboardKey}KeyboardActive`, boolean>,
    Record<`${ExclusiveSymbolKey}ExclusiveSymbolActive`, boolean> {
  name: string;
  email: string;
  fullAddress: string;
  phone: string;
  storeCode: string;
  currency: string | null;
  workshopSav: string | null;
  workshopEvent: string | null;
  workshopEventStart: string | null;
  workshopEventEnd: string | null;
  country: string | null;
  region: string | null;
  stockControl: boolean;
  bypassHeadOfficeApproval: boolean;
  bypassBoutiqueManagerApproval: boolean;
  permanentInStoreWorkshop: boolean;
  disabled: boolean;
  centralFlowForbidden: boolean;
  abcdiorActive: boolean;
  dmsActive: boolean;
  iosKeyboardActive: boolean;
  symbolActive: boolean;
  exclusiveSymbolActive: {
    active: boolean;
    symbols: Record<ExclusiveSymbolKey, boolean>;
  };
  iconicThreadsActive: boolean;
  exclusiveThreadsActive: boolean;
  workshopSavId: string | null;
  workshopEventId: string | null;
  frontOfficeAccess: { abcdior: boolean; dms: boolean };
  iosKeyboard: {
    active: boolean;
    keyboard: Record<StoreKeyboardKey, boolean>;
  };
}

export const createStore = (model: StoreInput) =>
  api.post<unknown>('store', model);
export const updateStore = (model: WithId<StoreInput>) =>
  api.put<unknown>('store', model);
